import React from "react";
import { Grid, Divider, withStyles } from "@material-ui/core";

import { v4 as uuidv4 } from "uuid";
import constants from "./constants";
import {
  Action,
  AddTrailer,
  DeleteTrailer,
  DispatchInput,
  Detail,
  Header,
  Notes,
  DefectDetails,
} from "./components";
import {
  getTractorError,
  getDollyError,
  getTrailerError,
  getUpdatedTrailerError,
  getEquipmentStatusesVisibleForDispatch,
  getUniqueEquipmentNumber,
  getEquipmentStatusesWithAllowedDispatch,
} from './utils';
import { EQUIPMENT_DEFECTS_GRID_FILTER } from "./EquipmentDefects/constants";
import {
  getRouteNameDisplayValue,
  ensureTrailerRouteInOptions,
  resolveBaseRouteOptions,
  mergeUserCreatedRoutes,
  sanitizeRouteNameToAlphanumeric,
  shouldShowAddRouteOption,
  resolveRouteOnTrailerChange,
  getRouteOptionsForDuplicateCheck,
  getRouteOptionsIncludingFromAllTrailers,
} from "./routeNameUtils";
import { ALPHA_NUMERIC_REGEX } from "../constRegex";
import { isEqual } from "lodash/lang";
import compose from "recompose/compose";
import { withAppConfig } from "../../../../../appConfigContext";

const styles = (theme) => ({
  container: {
    flexGrow: 1,
    overflowY: "auto",
  },
  detailContainer: {
    padding: "0px 20px",
  },
  divider: {
    backgroundColor: theme.gridIconBorderColor,
    opacity: 0.6,
  },
});

class DispatchTrailer extends React.Component {
  constructor(props) {
    super(props);
    this.generateTrailerList();
    this.userCreatedRoutesByTrailer = {};
    this.state = {
      trailerDetails: [this.getNewTrailer()],
      trailerDetailsErrors: [null],
      tractor: null,
      notes: "",
      tractorError: null,
      isLoading: props.isLoading ?? true, // Initialize based on props to handle remounting
    };
  }

  generateTrailerList = () => {
    this.trailerListWithoutStraightTruck = this.props.trailerList.filter(
      (trailer) =>  !trailer.isStraightTruck
    );
  }

  componentWillUnmount() {
    this.trailerListWithoutStraightTruck = null;
    this.userCreatedRoutesByTrailer = null;
  }
  
  componentDidUpdate(prevProps){
    if(prevProps.allTrailers !== this.props.allTrailers){
      this.generateTrailerList();
      if (this.state.isLoading && !this.props.isLoading) {
        this.setState({
          isLoading: this.props.isLoading
        }, this.processPendingRouteSelections)  // Process stored route selections after equipment data loads
      } else {
        this.updateState(); //Update equipment due to signalR trigger
      }
    }
  }

  processPendingRouteSelections = () => {
    // Process route names stored in trailerDetails
    this.state.trailerDetails.forEach((trailer, index) => {
      if (trailer.routeName) {
        this.updateRouteName(index, true)({ value: trailer.routeName });
      }
    });
  }

  updateState = () => {
    this.setState((state) => ({
      trailerDetails: state.trailerDetails.map(eachTrailer => {
        const trailer = this.props.allTrailers.find(
          (equipment) => equipment.trailerId === eachTrailer?.trailerId
        ) ?? this.getNewTrailer();
        const dolly = this.props.dollyList.find(eachDolly => eachDolly.value === eachTrailer.dolly?.value) ?? null;
        return {
          ...trailer,
          dolly
        }
      }),
      tractor: this.props.tractorList.find(tractor =>
            tractor.value === state.tractor?.value
          ) ?? null
    }), this.refreshErrors)
  }

  refreshErrors = ()=>{
    // Don't calculate errors when loading is true
    if (this.state.isLoading) {
      return;
    }
    
    this.refreshTrailerError()
     if (this.state.tractor) {
       this.setState((state)=>({
         tractorError: getTractorError({
          tractor: state.tractor,
          tractorStatusConfig: this.props.tractorStatusConfig,
          isStraightTruckTrailer: this.checkIfStraightTruck(this.state.trailerDetails)
        }),
       }));
     }
  }

  getTrailerDetails = () => {
    if (this.props.configJSON.allowAnyStraightTruckInDriverDispatch && this.state.trailerDetails[0]?.isStraightTruck) {
      return this.state.trailerDetails[0];
    }
    return this.getNewTrailer();
  };

  getNewTrailer = () => {
    return {
      id: uuidv4(),
      trailerNumber: null,
      manifestNumber: null,
      routeName: null,
      dolly: null,
    };
  };

  updateNotes = (notes) => {
    this.setState({
      notes,
    });
  };

  // function to find matching straight tractor for a trailer
  findMatchingStraightTractor = (trailerData) => {
    if (trailerData?.isStraightTruck) {
      const uniqueTrailerNumber = getUniqueEquipmentNumber(trailerData.trailerNumber, trailerData?.trailerScac);
      const straightTractor = this.props.tractorList.find(
        (tractor) => {
          const uniqueTractorNumber = getUniqueEquipmentNumber(tractor.label, tractor?.tractorScac);
          return uniqueTractorNumber === uniqueTrailerNumber
        }) ?? null;
      return straightTractor;
    }
    return null;
  };

  // function to handle straight truck trailer replacement
  handleStraightTruckReplacement = (updateIndex, trailerData) => {
    const matchingTractor = this.findMatchingStraightTractor(trailerData);    
    if (this.props.configJSON.allowAnyStraightTruckInDriverDispatch && !matchingTractor) {
      // No matching tractor found and flag is enabled, update trailer and call error validation
      this.setState(
        (state) => ({
          trailerDetails: state.trailerDetails.map((trailer, index) =>
            index === updateIndex ? trailerData : trailer
          )
        }), () => {
        this.updateTrailerError(updateIndex);
        this.onChangeTractor(matchingTractor);
      });
    } else {
      // Matching tractor found, just update the tractor
      this.onChangeTractor(matchingTractor);
    }
  };

  // function to handle regular trailer replacement
  handleRegularTrailerReplacement = (updateIndex, trailerData) => {
    const trailerDetails =  this.state.trailerDetails.map((trailer, index) =>
      index === updateIndex ? trailerData : trailer
    );
    // Clear tractor if it was a straight truck and trailer changed
    const tractor = this.state.tractor?.isStraightTruck ? null : this.state.tractor;
    this.updateTrailerDetails( trailerDetails, tractor);
  };

  replaceTrailer = (updateIndex, data) => {
    const trailerData = data || this.getNewTrailer();    
    // Handle straight truck trailers differently
    if (trailerData?.isStraightTruck) {
      this.handleStraightTruckReplacement(updateIndex, trailerData);
    } else {
      this.handleRegularTrailerReplacement(updateIndex, trailerData);
    }
  };

  getValidTrailersByRoute = (routeName) => {
    const {allTrailers, trailerStatusConfig} = this.props;
    const trailerStatusInOrder = getEquipmentStatusesVisibleForDispatch(trailerStatusConfig);
    return allTrailers.filter(
        (trailer) =>
          trailerStatusInOrder.includes(trailer.trailerStatus) &&
          trailer.routeName?.slice(-constants.ROUTE_NAME_SUFFIX_LENGTH) === routeName
      ) ?? [];    
  };

  // Merges API trailer data with current state; for closed trailers re-selected via spurious Select update, preserves user's routeName over API value.
  getMergedTrailerData = (trailerData, currentTrailer) => {
    const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
    const isSameTrailer = currentTrailer?.trailerId?.toLowerCase() === trailerData?.trailerId?.toLowerCase();
    const isClosed = enableNewRoute && this.isClosedTrailer(trailerData?.trailerStatus);
    const merged = (isClosed && isSameTrailer)
      ? { ...trailerData, routeName: currentTrailer?.routeName }
      : trailerData;
    return { merged, hasChanged: !isEqual(merged, currentTrailer) };
  };

  updateTrailerNumber = (updateIndex) => (value) => {
    const trailerId = value?.value;
    const trailerData = this.props.allTrailers.find(
      (equipment) => equipment.trailerId?.toLowerCase() === trailerId?.toLowerCase()
    );
    const currentTrailer = this.state.trailerDetails[updateIndex];
    const { merged, hasChanged } = this.getMergedTrailerData(trailerData, currentTrailer);
    //dont replace trailer if same trailer selected again
    if (hasChanged) {
      const resolvedRouteName = resolveRouteOnTrailerChange({
        currentTrailer,
        newTrailerData: merged,
        userCreatedRoutesByTrailer: this.userCreatedRoutesByTrailer,
        enableNewRouteAssignmentForTrailer: Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer),
      });
      this.replaceTrailer(updateIndex, { ...merged, routeName: resolvedRouteName });
    }
  };

  isEmptyTrailer = (trailerStatus, routeId) => {
    const EMPTY_TRAILER_STATUS = this.props.trailerStatusConfig.find(
      (trailerCong) => trailerCong.rmaxCode === constants.RMAX_EMPTY_TRAILER_CODE
    )?.status;
    return trailerStatus === EMPTY_TRAILER_STATUS && !routeId;
  };

  isClosedTrailer = (trailerStatus) => {
    const CLOSED_TRAILER_STATUS = this.props.trailerStatusConfig?.find(
      (c) => c.rmaxCode === constants.RMAX_CLOSED_TRAILER_CODE
    )?.status;
    return trailerStatus === CLOSED_TRAILER_STATUS;
  };

  // When user picks a route, we either (a) fill the slot with a trailer that has that route, or (b) just set the route on the trailer already in the slot.
  // (a) Pre-select: slot has no trailer or is an empty trailer → replace slot with the first trailer that has the selected route.
  // (b) No pre-select: slot already has a trailer (e.g. 931282UPGF with DEFAULTROUTE) → keep that trailer and only update its route (do not swap to another trailer that has the route).
  shouldPreSelectTrailerForRoute = (selectedTrailer, preSelectRouteTrailers) => {
    return preSelectRouteTrailers?.length > 0 &&
    (this.isEmptyTrailer(selectedTrailer.trailerStatus, selectedTrailer.routeId) || !selectedTrailer?.trailerId);
  }

  storeRouteNameDuringLoading = (updateIndex, routeName) => {
    this.setState((state) => ({
      trailerDetails: state.trailerDetails.map((trailer, index) =>
        index === updateIndex
          ? { ...trailer, routeName }
          : trailer
      )
    }));
  }

  // Stores user-created (Creatable) route names keyed by trailerId so they persist after switching routes and follow the trailer across slots.
  trackUserCreatedRoute = (trailerId, selection, routeName) => {
    if (selection?.isNewValue && routeName && trailerId) {
      const existing = this.userCreatedRoutesByTrailer[trailerId] || [];
      if (!existing.some((r) => r.value === routeName)) {
        this.userCreatedRoutesByTrailer[trailerId] = [...existing, { label: routeName, value: routeName }];
      }
    }
  };

  // Returns user-created routes for a trailer (by trailerId); empty when feature flag is off or no trailerId.
  getUserCreatedRoutes = (trailerId) => {
    const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
    return (enableNewRoute && trailerId) ? (this.userCreatedRoutesByTrailer?.[trailerId] || []) : [];
  };

  // Returns null for user-created routes (handled by mergeUserCreatedRoutes) to avoid suffix-based duplicates in ensureTrailerRouteInOptions.
  getTrailerRouteExcludingUserCreated = (trailer, userCreatedRoutes) => {
    const isUserCreated = userCreatedRoutes.some((r) => r.value === trailer?.routeName);
    return isUserCreated ? null : trailer?.routeName;
  };

  updateRouteName = (updateIndex, isPendingRouteSelection = false) => (value) => {
    const routeName = sanitizeRouteNameToAlphanumeric(value);
    const selectedTrailer = this.state.trailerDetails[updateIndex];

    this.trackUserCreatedRoute(selectedTrailer?.trailerId, value, routeName);

    // If loading is true, just store the route name in trailer state
    if (this.state.isLoading) {
      this.storeRouteNameDuringLoading(updateIndex, routeName)
      return;
    }

    let preSelectRouteTrailers = [];
    const selectedTrailerRoute = selectedTrailer?.routeName?.slice(-constants.ROUTE_NAME_SUFFIX_LENGTH);
    if (!this.isEmptyTrailer(selectedTrailer.trailerStatus, selectedTrailer.routeId)) { //pre select trailer for a selected route only if non empty trailers selected
      preSelectRouteTrailers = this.getValidTrailersByRoute(routeName);
    }
    const selectRouteTrailerData = this.getValidTrailersByRoute(selectedTrailerRoute).map(
      (trailer) => trailer.trailerId
    );
    const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
    // For closed trailer: clearing route must not clear trailer selection (only when feature flag is on)
    const isClosed = enableNewRoute && this.isClosedTrailer(selectedTrailer?.trailerStatus);
    const shouldClearTrailer =
      !isClosed &&
      routeName === null &&
      selectRouteTrailerData.length > 0 &&
      selectRouteTrailerData.includes(selectedTrailer?.trailerId);
    // When flag off: old behavior — replace trailer with one that has the selected route. When flag on: only replace when slot is empty or has no trailer (keep trailer e.g. DEFAULTROUTE case).
    const shouldPreSelectTrailer = enableNewRoute
      ? this.shouldPreSelectTrailerForRoute(selectedTrailer, preSelectRouteTrailers)
      : (preSelectRouteTrailers?.length > 0);
    const trailerData = shouldClearTrailer
        ? null
        : {
            ...selectedTrailer,
            ...(shouldPreSelectTrailer && { ...preSelectRouteTrailers[0] }),
            routeName
          };
    //dont replace trailer if same trailer selected again
    if (!isEqual(trailerData, selectedTrailer) || isPendingRouteSelection) {
      this.replaceTrailer(updateIndex, trailerData);
    }
  };

  updateDolly = (updateIndex) => (dolly) => {
    this.setState(
      (state) => ({
        trailerDetails: state.trailerDetails.map((trailer, index) =>
          index === updateIndex
            ? {
                ...trailer,
                dolly
              }
            : trailer
        )
      }),
      () => this.updateDollyError(updateIndex)
    );
  };

  updateDollyError = (updateIndex) => {
    this.setState((state) => ({
      trailerDetailsErrors: state.trailerDetailsErrors.map((error, index) => {
        if (index === updateIndex) {
          const dollyError = getDollyError({
            dolly: state.trailerDetails[index].dolly,
            trailersData: state.trailerDetails,
            dollyStatusConfig: this.props.dollyStatusConfig,
          });
          // update newly updated dolly error
          return {
            ...error,
            dolly: dollyError,
          };
        }
        // if any trailer had duplicate dolly error, re-evaulate error with new data
        if (error?.dolly === constants.ERROR_MESSAGES.DUPLICATE_DOLLY) {
          return {
            ...error,
            dolly: getDollyError({
              dolly: state.trailerDetails[index].dolly,
              trailersData: state.trailerDetails,
              dollyStatusConfig: this.props.dollyStatusConfig,
            }),
          };
        }
        return error;
      }),
    }));
  };

  updateTrailerError = (updateIndex) => {
    // Don't calculate errors when loading is true
    if (this.state.isLoading) {
      return;
    }
    
    const { trailerStatusConfig }=  this.props;
    this.setState((state) => ({
      trailerDetailsErrors: state.trailerDetails[0]?.isStraightTruck
        ? [
            {
              trailerNumber: getTrailerError({
                trailer: state.trailerDetails[0],
                trailersData: state.trailerDetails,
                trailerStatusConfig,
              }),
            },
          ]
        : state.trailerDetailsErrors.map((error, index) => {
            if (index === updateIndex) {
              const trailerError = getTrailerError({
                trailer: state.trailerDetails[index],
                trailersData: state.trailerDetails,
                trailerStatusConfig,
              });
              // update newly updated trailer error
              return {
                trailerNumber: trailerError,
              };
            }
            // if any trailer had duplicate trailer error, re-evaulate error with new data
            if (
              error?.trailerNumber ===
              constants.ERROR_MESSAGES.DUPLICATE_TRAILER
            ) {
              return {
                trailerNumber: getTrailerError({
                  trailer: state.trailerDetails[index],
                  trailersData: state.trailerDetails,
                  trailerStatusConfig,
                }),
              };
            }
            return error;
          }),
    }));
  };

  onChangeTractor = (tractor) => {
    const index=0;
    const tractorDetails = this.props.tractorList.find(
      (equipment) => equipment.value?.toLowerCase() === tractor?.value?.toLowerCase()
    );
    if (tractorDetails?.isStraightTruck &&
      this.state.tractor?.value?.toLowerCase() !== tractorDetails?.value?.toLowerCase()
    ) {
      const uniqueTractorNumber = getUniqueEquipmentNumber(tractorDetails.label, tractorDetails?.tractorScac);
      const TRAILER_VALID_STATUS = getEquipmentStatusesWithAllowedDispatch(this.props.trailerStatusConfig);
      const selectedTrailer = this.props.allTrailers
        .filter((trailer) => TRAILER_VALID_STATUS.includes(trailer?.trailerStatus))
        .find((equipment) => {
          const uniqueTrailerNumber = getUniqueEquipmentNumber(
            equipment.trailerNumber,
            equipment?.trailerScac
          );
          return uniqueTractorNumber === uniqueTrailerNumber;
        });
      const isStraightTrailer = selectedTrailer?.isStraightTruck;
      const trailerDetails = isStraightTrailer ? selectedTrailer : this.getTrailerDetails();
      // check if trailer is a straight trailer
      // if straight than update the trailer with matching tractor otherwise clear the dropdown of trailer
      if (this.state.trailerDetails[index]?.trailerNumber !== trailerDetails?.trailerNumber) {
        this.updateTrailerDetails([trailerDetails], tractorDetails)
        return;
      }
      this.updateTractor(tractorDetails);
    }else if(this.props.configJSON.allowAnyStraightTruckInDriverDispatch && this.isStraightTruckSelected(tractor, index)){      
      return;
    } else if (!tractorDetails || !tractorDetails?.isStraightTruck) {
      // check if new tractor selected is not straight but previously selected tractor was straight truck , then we need to clear the previous selected straight trailer also
      if (this.state.tractor?.isStraightTruck) {
        this.updateTrailerDetails([this.getTrailerDetails()], tractorDetails);
        return;
      }
      this.updateTractor(tractorDetails);
    }
  };

  isStraightTruckSelected = (tractor, index) => {
    // if flag is enabled 
    // and tractor is not selected/not matching 
    // and trailer is straight truck 
    // and already selected tractor also a straight truck then do nothing
    return !tractor && this.state.trailerDetails[index]?.isStraightTruck && this.state.tractor?.isStraightTruck;
  }


  updateTrailerDetails = (trailerDetails, tractor) => {
    this.setState(
      {
        trailerDetails,
      },
      () => {
        this.updateTrailerError(0);
        this.updateTractor(tractor);
      }
    );
  };

  updateTractor = (tractor) => {
    // Don't calculate errors when loading is true
    if (this.state.isLoading) {
      return;
    }
    
    this.setState({
      tractor,
      tractorError: getTractorError({
        tractor,
        tractorStatusConfig: this.props.tractorStatusConfig,
        isStraightTruckTrailer: this.checkIfStraightTruck(this.state.trailerDetails)
      }),
    });
  };

  addNewTrailer = () => {
    const newTrailerData = this.getNewTrailer();
    this.setState((state) => ({
      trailerDetails: [...state.trailerDetails, newTrailerData],
      trailerDetailsErrors: [...state.trailerDetailsErrors, null],
    }));
  };

  refreshTrailerError = () => {
    // Don't calculate errors when loading is true
    if (this.state.isLoading) {
      return;
    }
    
    this.setState({
      trailerDetailsErrors: getUpdatedTrailerError({
        trailerDetails: this.state.trailerDetails,
        trailerDetailsErrors: this.state.trailerDetailsErrors,
        trailerStatusConfig: this.props.trailerStatusConfig,
        dollyStatusConfig: this.props.dollyStatusConfig,
      })
    })
  }

  removeTrailer = (removeIndex) => {
    this.setState((state) => ({
      trailerDetails: state.trailerDetails.filter(
        (_trailer, index) => index !== removeIndex
      ),
      trailerDetailsErrors: state.trailerDetailsErrors.filter(
        (_error, index) => index !== removeIndex
      ),
    }), () => this.refreshTrailerError()
    );
  };

  getTrailerDetailsError = (trailer, index) => {
    const trailerError = getTrailerError({
      trailer, 
      trailersData: this.state.trailerDetails,
      trailerStatusConfig: this.props.trailerStatusConfig,
    });
    const dollyError = index
      ? getDollyError({dolly: trailer.dolly, trailersData: this.state.trailerDetails, dollyStatusConfig: this.props.dollyStatusConfig})
      : null;
    if (trailerError || dollyError) {
      return {
        trailerNumber: trailerError,
        dolly: dollyError,
      };
    }
    return null;
  };

  isValidTrailerDispatch = () => {
    const trailerDetailsErrors = [];
    this.state.trailerDetails.forEach((trailerDetail, index) =>
      trailerDetailsErrors.push(
        this.getTrailerDetailsError(trailerDetail, index)
      )
    );
    const tractorError = getTractorError({
      tractor: this.state.tractor,
      tractorStatusConfig: this.props.tractorStatusConfig,
      isStraightTruckTrailer: this.checkIfStraightTruck(this.state.trailerDetails)
    });
    this.setState({
      trailerDetailsErrors,
      tractorError,
    });
    return !Boolean(
      tractorError || trailerDetailsErrors.filter(Boolean).length
    );
  };

  checkIfStraightTruck = (trailerDetails) => {
    return (
      trailerDetails.length === 1 && trailerDetails[0].isStraightTruck
    );
  }

  dispatchTrailer = () => {
    const data = this.isValidTrailerDispatch()
      ? {
          tractorId: this.state.tractor?.value,
          notes: this.state.notes,
          trailerDetails: this.state.trailerDetails.map((trailer) => ({
            trailerId: trailer.trailerId,
            manifestNumber: trailer.manifestNumber || null,
            dollyId: trailer.dolly?.value || null,
            routeName: trailer.routeName || null,
          })),
        }
      : null;
    this.props.dispatchDriver(data);
  };

  renderDivider = (index) =>
    index <= this.state.trailerDetails.length - 1 ? (
      <Divider className={this.props.classes.divider} />
    ) : null;

  // Returns trailerIds whose user-created routes include the given routeName.
  getTrailerIdsForUserCreatedRoute = (routeName) => {
    if (!routeName || !this.userCreatedRoutesByTrailer) return [];
    return Object.keys(this.userCreatedRoutesByTrailer).filter(
      (trailerId) => (this.userCreatedRoutesByTrailer[trailerId] || []).some((r) => r.value === routeName)
    );
  };

  getFilteredTrailerOptions = (trailerIndex) => {
    let filteredTrailerOptions = [];
    const trailerDetailsAtIndex = this.state.trailerDetails[trailerIndex];
    const allTrailerOptions =
      trailerIndex > 0 ? this.trailerListWithoutStraightTruck : this.props.trailerList;
    if (this.isEmptyTrailer(trailerDetailsAtIndex.trailerStatus, trailerDetailsAtIndex.routeId)) {
      return allTrailerOptions;
    } else if (trailerDetailsAtIndex.routeName) {
      const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
      if (enableNewRoute) {
        const trailerIdsWithRoute = this.getTrailerIdsForUserCreatedRoute(trailerDetailsAtIndex.routeName);
        if (trailerIdsWithRoute.length > 0) {
          const filtered = allTrailerOptions.filter((t) =>
            trailerIdsWithRoute.some((id) => id?.toLowerCase() === t.value?.toLowerCase())
          );
          if (filtered.length > 0) return this.ensureSelectedTrailerInOptions({ filteredOptions: filtered, selectedTrailer: trailerDetailsAtIndex, allOptions: allTrailerOptions });
        }
      }
      filteredTrailerOptions = allTrailerOptions.filter(
        (trailer) =>
          trailer?.routeName?.slice(-constants.ROUTE_NAME_SUFFIX_LENGTH) ===
          trailerDetailsAtIndex.routeName?.slice(-constants.ROUTE_NAME_SUFFIX_LENGTH)
      );
      if (enableNewRoute && filteredTrailerOptions.length > 0) {
        return this.ensureSelectedTrailerInOptions({ filteredOptions: filteredTrailerOptions, selectedTrailer: trailerDetailsAtIndex, allOptions: allTrailerOptions });
      }
    }
    return filteredTrailerOptions?.length > 0 ? filteredTrailerOptions : allTrailerOptions;
  };

  // When user assigns a route to a trailer that didn't have it (e.g. DEFAULTROUTE → 014), the filtered-by-route list may exclude the selected trailer. Include it so the Trailer dropdown does not clear.
  ensureSelectedTrailerInOptions = ({ filteredOptions, selectedTrailer, allOptions }) => {
    if (!selectedTrailer?.trailerId) return filteredOptions;
    const alreadyInList = filteredOptions.some((t) => t.value?.toLowerCase() === selectedTrailer.trailerId?.toLowerCase());
    if (alreadyInList) return filteredOptions;
    const currentOption = allOptions.find((t) => t.value?.toLowerCase() === selectedTrailer.trailerId?.toLowerCase());
    return currentOption ? [currentOption, ...filteredOptions] : filteredOptions;
  };

  getFilteredRouteOptions = (routeIndex) => {
    let routeOptionsResult;
    let filteredRouteOptions = [];
    const trailerDetailsAtIndex = this.state.trailerDetails[routeIndex];
    const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
    const isClosed = enableNewRoute && this.isClosedTrailer(trailerDetailsAtIndex.trailerStatus);
    const userCreatedRoutes = this.getUserCreatedRoutes(trailerDetailsAtIndex?.trailerId);
    const trailerRouteExcludingUserCreated = this.getTrailerRouteExcludingUserCreated(trailerDetailsAtIndex, userCreatedRoutes);
    if (this.isEmptyTrailer(trailerDetailsAtIndex.trailerStatus, trailerDetailsAtIndex.routeId)) {
      routeOptionsResult = ensureTrailerRouteInOptions({
        routeOptions: this.props.routeList,
        trailerRouteName: trailerRouteExcludingUserCreated,
      });
    }
    // Closed trailer with no route (user cleared): show all available routes
    else if (isClosed && !trailerDetailsAtIndex?.routeName) {
      routeOptionsResult = ensureTrailerRouteInOptions({
        routeOptions: this.props.routeList,
        trailerRouteName: null,
      });
    } 
    // Closed with route OR non-closed: show trailer-associated filtered routes (from mapping + all trailers)
    else {
      if (trailerDetailsAtIndex?.routeName || trailerDetailsAtIndex?.trailerId) {
        const selectedRouteSuffix = trailerDetailsAtIndex.routeName?.slice(-constants.ROUTE_NAME_SUFFIX_LENGTH);
        const matchedTrailerOptions = this.props.trailerList
          .filter(
            (trailer) =>
              trailer.routeName === selectedRouteSuffix ||
              trailer.value === trailerDetailsAtIndex.trailerId
          )
          .map(
            (trailer) =>
              trailer.routeName?.slice(-constants.ROUTE_NAME_SUFFIX_LENGTH) || null
          );
        const baseRouteList = getRouteOptionsIncludingFromAllTrailers({
          routeList: this.props.routeList,
          allTrailers: this.props.allTrailers,
          suffixLength: constants.ROUTE_NAME_SUFFIX_LENGTH,
        });
        filteredRouteOptions = baseRouteList.filter((route) =>
          matchedTrailerOptions.includes(route.value)
        );
      }
      const routeOptions = resolveBaseRouteOptions({
        filteredRouteOptions,
        trailer: trailerDetailsAtIndex,
        routeList: this.props.routeList,
        trailerList: this.props.trailerList,
      });
      routeOptionsResult = ensureTrailerRouteInOptions({
        routeOptions,
        trailerRouteName: trailerRouteExcludingUserCreated,
      });
    }
    return mergeUserCreatedRoutes(routeOptionsResult, userCreatedRoutes);
  };

  render() {
    const {
      trailerDetails,
      trailerDetailsErrors,
    } = this.state;
    const { dollyList, tractorList, isPreselectedDriver, maxAllowedTrailerCount } = this.props;

    const isStraightTruckSelected = this.checkIfStraightTruck(trailerDetails) || this.state.tractor?.isStraightTruck

    const addTrailerCount = isStraightTruckSelected
      ? 0
      : maxAllowedTrailerCount - trailerDetails.length;
    const addTrailers = new Array(addTrailerCount).fill("addTrailers");

    const { classes } = this.props;
    return (
      <>
        <Grid className={`${classes.container} customScroll`}>
          <Header label="TRAILER DETAILS" />
          {trailerDetails.map((trailer, trailerIndex) => (
            <React.Fragment key={trailer.id}>
              {/* Shows delete option for from 2nd trailer */}
              {trailerIndex ? (
                <DeleteTrailer id={trailerIndex} onClick={this.removeTrailer} />
              ) : null}
              <DispatchInput
                label="Trailer #"
                autoFocus={isPreselectedDriver}
                value={trailer.trailerNumber ?? null}
                options={this.getFilteredTrailerOptions(trailerIndex)}
                onChange={this.updateTrailerNumber(trailerIndex)}
                error={trailerDetailsErrors?.[trailerIndex]?.trailerNumber}
                isLoading={this.state.isLoading}
                isClearable
              />
              {Boolean(trailer?.defects?.length) && (
                <DefectDetails
                  defects={trailer?.defects}
                  equipmentType="trailers"
                  openDvirReports={this.props.openDvirReports(
                    EQUIPMENT_DEFECTS_GRID_FILTER.trailer
                  )}
                />
              )}
              <DispatchInput
                label="Manifest #"
                value={trailer.manifestNumber ?? null}
                disableEdit
              />
              {this.props.routeList.length > 0 ? (
                <DispatchInput
                  label="Route Name"
                  autoFocus={false}
                  value={getRouteNameDisplayValue({
                    routeName: trailer.routeName,
                    enableNewRouteAssignmentForTrailer: Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer),
                    routeOptions: this.getFilteredRouteOptions(trailerIndex),
                  })}
                  options={this.getFilteredRouteOptions(trailerIndex)}
                  onChange={this.updateRouteName(trailerIndex)}
                  isClearable
                  canAcceptNewValue={shouldShowAddRouteOption({
                    trailer,
                    enableNewRouteAssignmentForTrailer: this.props.configJSON?.enableNewRouteAssignmentForTrailer,
                    isClosedTrailer: this.isClosedTrailer(trailer.trailerStatus),
                  })}
                  createLabel={
                    shouldShowAddRouteOption({
                      trailer,
                      enableNewRouteAssignmentForTrailer: this.props.configJSON?.enableNewRouteAssignmentForTrailer,
                      isClosedTrailer: this.isClosedTrailer(trailer.trailerStatus),
                    })
                      ? "Route"
                      : undefined
                  }
                  allOptions={Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer) ? getRouteOptionsForDuplicateCheck({
                    routeList: this.props.routeList,
                    allTrailers: this.props.allTrailers,
                    userCreatedRoutes: this.getUserCreatedRoutes(trailer?.trailerId),
                    suffixLength: constants.ROUTE_NAME_SUFFIX_LENGTH,
                  }) : undefined}
                  format={ALPHA_NUMERIC_REGEX}
                  maxLength={constants.ROUTE_NAME_MAX_LENGTH}
                />
              ) : (
                <DispatchInput
                  label="Route Name"
                  value={trailer.routeName ?? null}
                  disableEdit
                />
              )}
              {/* Dolly needed only for 2nd or more trailers are attached */}
              {trailerIndex > 0 ? (
                <>
                  <DispatchInput
                    label="Dolly #"
                    autoFocus={false}
                    value={trailer.dolly?.label ?? null}
                    options={dollyList}
                    onChange={this.updateDolly(trailerIndex)}
                    error={trailerDetailsErrors?.[trailerIndex]?.dolly}
                    isLoading={this.state.isLoading}
                    isClearable
                  />
                  {Boolean(trailer.dolly?.defects?.length) && (
                    <DefectDetails
                      defects={trailer.dolly?.defects}
                      equipmentType="dolly"
                      openDvirReports={this.props.openDvirReports(
                        EQUIPMENT_DEFECTS_GRID_FILTER.dolly
                      )}
                    />
                  )}
                </>
              ) : null}
              <Grid container className={classes.detailContainer}>
                <Detail
                  label="Bills"
                  value={trailer.numberOfProsLoaded}
                  inline
                />
              </Grid>
              <Grid container className={classes.detailContainer}>
                <Detail
                  label="Trailer Type"
                  value={trailer.trailerName ?? "__"}
                />
                <Detail
                  label="Trailer Status"
                  value={trailer.trailerStatus ?? "__"}
                />
                <Detail
                  label="Ideal Start Time"
                  value={trailer.idealStartTime ?? "__:__"}
                />
              </Grid>
              {this.renderDivider(trailerIndex)}
            </React.Fragment>
          ))}

          {addTrailers.map((_newTrailer, trailerIndex) => (
            <AddTrailer key={trailerIndex} onClick={this.addNewTrailer} />
          ))}
          <Header label="TRACTOR DETAILS" />
          <DispatchInput
            label="Tractor #"
            autoFocus={false}
            value={this.state.tractor?.label ?? null}
            options={tractorList}
            onChange={this.onChangeTractor}
            isClearable
            error={this.state.tractorError}
            isLoading={this.state.isLoading}
          />
          {Boolean(this.state.tractor?.defects?.length) && (
            <DefectDetails
              defects={this.state.tractor.defects}
              equipmentType="tractors"
              openDvirReports={this.props.openDvirReports(
                EQUIPMENT_DEFECTS_GRID_FILTER.tractor
              )}
            /> 
          )}
          <Notes value={this.state.notes} onChange={this.updateNotes} />

          {this.props.children}
        </Grid>
        <Action
          cancel={this.props.cancel}
          confirm={this.dispatchTrailer}
          disableButton={this.props.disableDispatch || this.state.isLoading}
        />
      </>
    );
  }
}

export default compose(withAppConfig, withStyles(styles))(DispatchTrailer);
